import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Banner,
  Breadcrumbs,
  Button,
  LayerCard,
  Loader,
  Switch,
  useKumoToastManager,
  type TabsItem,
} from '@cloudflare/kumo'
import { CalendarPlusIcon, HouseIcon } from '@phosphor-icons/react'
import type {
  AvailabilitySlot,
  Building,
  MyRoomView,
  RoomBookingDto,
  RoomType,
} from '@hub-wifi/shared'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { DayNavigator } from '../components/day-navigator/day-navigator'
import { RoomTimeline, type RoomTimelineSelection } from '../components/booking-form/room-timeline'
import { describeBookingError } from '../components/booking-form/utils'
import { isoAtBuildingTz } from '../lib/calendar-time'

// Re-exported for `RoomBrowsePage.iso.test.ts` (the helper lives in lib now).
export { isoAtBuildingTz }

type PerRoomState = {
  slots: AvailabilitySlot[] | null
  loading: boolean
  error: string | null
}

interface Selection {
  roomId: string
  range: RoomTimelineSelection
}

const DATE_DEBOUNCE_MS = 250
const EVENING_CUTOFF_HOUR = 18

/** YYYY-MM-DD in the given IANA timezone for `instant`. `en-CA` formats as ISO. */
function toIsoDateInTz(instant: Date, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant)
}

/** Hour-of-day (0-23) in the given IANA timezone for `instant`. */
function hourInTz(instant: Date, timezone: string): number {
  const hh = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    hour: '2-digit',
    hour12: false,
  }).format(instant)
  return Number(hh)
}

/** Add `days` to a YYYY-MM-DD date string (timezone-safe — we only do calendar math). */
function shiftIsoDate(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  // Use UTC math to avoid DST shifts on the day-of-month arithmetic itself;
  // the timezone is what `toIsoDateInTz` reapplied above when computing the
  // baseline, so calendar arithmetic in UTC is the safe move.
  const utc = Date.UTC(y!, m! - 1, d! + days)
  const dt = new Date(utc)
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/** Default-date logic: if local-tz hour ≥ 18, default to tomorrow; else today. */
function computeDefaultDate(timezone: string, now: Date = new Date()): string {
  const today = toIsoDateInTz(now, timezone)
  if (hourInTz(now, timezone) >= EVENING_CUTOFF_HOUR) {
    return shiftIsoDate(today, 1)
  }
  return today
}

/**
 * Guards `Intl.DateTimeFormat({ timeZone })` against empty/invalid strings.
 * Falls back to UTC — better to render the wrong tz than crash the whole page.
 */
function safeTimezone(tz: string | null | undefined): string {
  if (!tz) return 'UTC'
  try {
    // Smoke-test by constructing a formatter.
    new Intl.DateTimeFormat('en-CA', { timeZone: tz })
    return tz
  } catch {
    return 'UTC'
  }
}

/** `YYYY-MM-DDT00:00:00<offset>` for a given date + IANA timezone. */
function isoDateAtMidnight(isoDate: string, timezone: string): string {
  // Build a Date at midnight UTC and ask Intl for the offset at that instant
  // in `timezone`. Works for our use case (the input date corresponds to
  // "00:00 local"); a DST-jump edge case still produces a defensible value.
  const utcMidnight = new Date(`${isoDate}T00:00:00Z`)
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  })
  const parts = fmt.formatToParts(utcMidnight)
  const tzName = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+00:00'
  const m = /GMT([+-])(\d{1,2})(?::?(\d{2}))?/.exec(tzName)
  const offset = m && m[2] ? `${m[1]}${m[2].padStart(2, '0')}:${m[3] ?? '00'}` : '+00:00'
  return `${isoDate}T00:00:00${offset}`
}

interface MyBuildingResponse {
  building:
    | (Building & {
        rooms: Array<Omit<MyRoomView, 'bookable'> & { mailbox?: string | null }>
      })
    | null
}

interface MyRoomsResponse {
  rooms: MyRoomView[]
}

interface AvailabilityResponse {
  slots: AvailabilitySlot[]
}

// Canonical display order for the type filter tabs — mirrors the Select
// options in RoomFormPage so the ordering stays consistent across the app.
const ROOM_TYPE_ORDER: RoomType[] = ['meeting', 'phone-booth', 'event', 'private-office']

const ALL_ROOM_TYPES = 'all'

export function RoomBrowsePage(): JSX.Element {
  const { getAccessTokenSilently } = useAuth0()
  const { t, locale } = useTranslation()
  const navigate = useNavigate()
  const toasts = useKumoToastManager()

  const [building, setBuilding] = useState<Building | null>(null)
  const [rooms, setRooms] = useState<MyRoomView[]>([])
  const [date, setDate] = useState<string>('') // YYYY-MM-DD; '' until building loads
  const [showFullDay, setShowFullDay] = useState(false)
  const [perRoom, setPerRoom] = useState<Record<string, PerRoomState>>({})
  const [selection, setSelection] = useState<Selection | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pageError, setPageError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>(ALL_ROOM_TYPES)
  // Live ticker for the "now" indicator inside each <RoomTimeline>. 60 s is
  // the smallest interval that still reflects the 30-min cell granularity
  // without thrashing reconciliation across N rooms.
  const [nowMs, setNowMs] = useState<number>(() => Date.now())

  // Debounce the date input.
  const debouncedDateRef = useRef<{ value: string; timer: number | null }>({
    value: '',
    timer: null,
  })

  // Memoize the validated timezone so `Intl.DateTimeFormat({timeZone: tz})` never throws.
  const tz = useMemo(() => safeTimezone(building?.timezone), [building?.timezone])

  // Stable ref to `t` so async callbacks don't have to depend on it. Locale
  // switches re-render the page but must NOT trigger refetch/refanout.
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  // Live ticker for the "now" indicator. Refresh every 60 s and clear on
  // unmount. The indicator itself only renders when the selected `date` is
  // today in the building tz (see <RoomTimeline> showNow guard).
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  // Initial load — `/api/my/building` (for timezone) + `/api/my/rooms`.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const [buildingResp, roomsResp] = await Promise.all([
          apiRequest<MyBuildingResponse>('/api/my/building', { token }),
          apiRequest<MyRoomsResponse>('/api/my/rooms', { token }),
        ])
        if (cancelled) return
        if (!buildingResp.building) {
          setPageError(tRef.current('roomBrowse.noBuilding'))
          return
        }
        const b: Building = (() => {
          const { rooms: _rooms, ...rest } = buildingResp.building
          return rest as Building
        })()
        setBuilding(b)
        setRooms(roomsResp.rooms)
        setDate(computeDefaultDate(safeTimezone(b.timezone)))
      } catch (err) {
        if (!cancelled) setPageError(describeBookingError(err, tRef.current))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently])

  // Clear any pending selection whenever the date changes — otherwise the user
  // could confirm a selection against a date that's no longer on the timeline.
  useEffect(() => {
    setSelection(null)
  }, [date])

  // Fan-out availability whenever date changes (debounced).
  const triggerFanout = useCallback(
    (effectiveDate: string) => {
      if (!building || rooms.length === 0) return
      const buildingTz = safeTimezone(building.timezone)
      const from = isoDateAtMidnight(effectiveDate, buildingTz)
      const to = isoDateAtMidnight(shiftIsoDate(effectiveDate, 1), buildingTz)
      // Seed per-room loading flags.
      setPerRoom((prev) => {
        const next: Record<string, PerRoomState> = { ...prev }
        for (const r of rooms) {
          next[r.id] = r.bookable
            ? { slots: null, loading: true, error: null }
            : { slots: [], loading: false, error: null }
        }
        return next
      })
      const bookable = rooms.filter((r) => r.bookable)
      void (async () => {
        // Token acquisition can fail (revoked session, network). If it does,
        // mark every bookable row as errored so the user can retry instead of
        // staring at a permanent spinner.
        let token: string
        try {
          token = await getAccessTokenSilently()
        } catch (err) {
          const msg = describeBookingError(err, tRef.current)
          setPerRoom((prev) => {
            const next: Record<string, PerRoomState> = { ...prev }
            for (const r of bookable) {
              next[r.id] = { slots: null, loading: false, error: msg }
            }
            return next
          })
          return
        }
        const fetchers = bookable.map(async (r) => {
          try {
            const qs = new URLSearchParams({ from, to, intervalMinutes: '30' })
            const resp = await apiRequest<AvailabilityResponse>(
              `/api/my/rooms/${r.id}/availability?${qs}`,
              { token },
            )
            return { roomId: r.id, slots: resp.slots, error: null as string | null }
          } catch (err) {
            return {
              roomId: r.id,
              slots: null,
              error: describeBookingError(err, tRef.current),
            }
          }
        })
        const results = await Promise.allSettled(fetchers)
        setPerRoom((prev) => {
          const next: Record<string, PerRoomState> = { ...prev }
          results.forEach((res, i) => {
            const roomId = bookable[i]!.id
            if (res.status === 'fulfilled') {
              next[roomId] = {
                slots: res.value.slots,
                loading: false,
                error: res.value.error,
              }
            } else {
              // The fetcher itself swallows API errors, so a rejected promise
              // here is something unexpected (e.g. a synchronous throw inside
              // the closure). Surface a generic error rather than leaving the
              // row stuck on the spinner.
              next[roomId] = { slots: null, loading: false, error: tRef.current('common.error') }
            }
          })
          return next
        })
      })()
    },
    [building, getAccessTokenSilently, rooms],
  )

  // Watch date changes (debounced).
  useEffect(() => {
    if (!date) return
    const cur = debouncedDateRef.current
    if (cur.timer !== null) window.clearTimeout(cur.timer)
    cur.value = date
    cur.timer = window.setTimeout(() => {
      triggerFanout(cur.value)
      cur.timer = null
    }, DATE_DEBOUNCE_MS) as unknown as number
    return () => {
      if (cur.timer !== null) window.clearTimeout(cur.timer)
    }
  }, [date, triggerFanout])

  const dayStart = useMemo(() => {
    if (!date || !building) return null
    return new Date(isoDateAtMidnight(date, tz))
  }, [date, building, tz])

  // `/my/bookings/new` is the booking-creation entry point — block past dates.
  const todayIso = toIsoDateInTz(new Date(), tz)

  const startHour = showFullDay ? 0 : 8
  const endHour = showFullDay ? 24 : 20

  // Room-type filter tabs — only worth showing when the building actually
  // mixes more than one room type; otherwise "All" + a single type tab would
  // add a control that can't change anything.
  const presentRoomTypes = useMemo(
    () => ROOM_TYPE_ORDER.filter((rt) => rooms.some((r) => r.type === rt)),
    [rooms],
  )
  const typeTabs: TabsItem[] | undefined = useMemo(() => {
    if (presentRoomTypes.length < 2) return undefined
    return [
      { value: ALL_ROOM_TYPES, label: t('common.all') },
      ...presentRoomTypes.map((rt) => ({ value: rt, label: t(`rooms.type.${rt}`) })),
    ]
  }, [presentRoomTypes, t])
  const visibleRooms = useMemo(
    () => (typeFilter === ALL_ROOM_TYPES ? rooms : rooms.filter((r) => r.type === typeFilter)),
    [rooms, typeFilter],
  )

  function handleTypeFilterChange(value: string) {
    setTypeFilter(value)
    // The active selection may belong to a room that's about to be filtered
    // out of view — drop it so the confirm bar can't reference a hidden row.
    setSelection(null)
  }

  function setSelectionForRoom(roomId: string, range: RoomTimelineSelection | null) {
    if (range === null) {
      setSelection((prev) => (prev?.roomId === roomId ? null : prev))
      return
    }
    setSelection({ roomId, range })
  }

  function retryRow(roomId: string) {
    if (!building || !date) return
    setPerRoom((prev) => ({
      ...prev,
      [roomId]: { slots: null, loading: true, error: null },
    }))
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const from = isoDateAtMidnight(date, tz)
        const to = isoDateAtMidnight(shiftIsoDate(date, 1), tz)
        const qs = new URLSearchParams({ from, to, intervalMinutes: '30' })
        const resp = await apiRequest<AvailabilityResponse>(
          `/api/my/rooms/${roomId}/availability?${qs}`,
          { token },
        )
        setPerRoom((prev) => ({
          ...prev,
          [roomId]: { slots: resp.slots, loading: false, error: null },
        }))
      } catch (err) {
        setPerRoom((prev) => ({
          ...prev,
          [roomId]: { slots: null, loading: false, error: describeBookingError(err, tRef.current) },
        }))
      }
    })()
  }

  async function confirmSelection() {
    if (!selection || !building || !date) return
    setSubmitting(true)
    const { roomId, range } = selection
    // Build ISO strings in the BUILDING's timezone, not the browser's. The
    // selection's epoch ms is an absolute instant; we ship it as the
    // building-local wall-clock + offset so the server's `new Date(...)`
    // round-trip preserves the meeting time the user actually picked.
    const bookingPromise = (async () => {
      const token = await getAccessTokenSilently()
      const startAt = isoAtBuildingTz(range.startMs, tz)
      const endAt = isoAtBuildingTz(range.endMs, tz)
      return apiRequest<RoomBookingDto>('/api/my/bookings', {
        method: 'POST',
        token,
        body: { roomId, startAt, endAt },
      })
    })()

    void toasts.promise(bookingPromise, {
      loading: { title: t('roomBrowse.creatingBooking') },
      success: (dto) => ({
        variant: 'success',
        title: t('roomBrowse.bookingCreated'),
        description: `${dto.roomName ?? dto.roomId} · ${new Date(dto.startAt).toLocaleString(locale)}`,
        timeout: 10_000,
        actions: [
          {
            children: t('roomBrowse.inviteGuest'),
            variant: 'secondary',
            size: 'sm',
            onClick: () => navigate(`/my/guests/new?roomBookingId=${dto.id}`),
          },
        ],
      }),
      error: (err) => ({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describeBookingError(err, t),
      }),
    })

    try {
      await bookingPromise
      navigate('/my/bookings')
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Room declined — refetch this row to surface the new busy block.
        retryRow(roomId)
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ----- Main browse page -----

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link href="/my/bookings">{t('myBookings.title')}</Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('roomBrowse.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('roomBrowse.title')}
        titleIcon={<CalendarPlusIcon size={24} className="text-kumo-default" />}
        description={t('roomBrowse.helper')}
        tabs={typeTabs}
        defaultTab={typeFilter}
        onValueChange={handleTypeFilterChange}
      />

      {pageError && (
        <Banner variant="error" className="mb-3">
          {pageError}
        </Banner>
      )}

      {!building && !pageError && (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      )}

      {building && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <DayNavigator
              value={date ?? ''}
              timezone={tz}
              min={todayIso}
              onChange={(ymd) => setDate(ymd)}
              onPrev={() => setDate((d) => (d ? shiftIsoDate(d, -1) : d))}
              onNext={() => setDate((d) => (d ? shiftIsoDate(d, 1) : d))}
              disabledPrev={!date || date <= todayIso}
              prevLabel={t('roomBrowse.prevDay')}
              nextLabel={t('roomBrowse.nextDay')}
              openLabel={t('dayNavigator.openCalendar')}
              todayLabel={t('dayNavigator.today')}
            />
            <label className="flex items-center gap-2">
              <Switch checked={showFullDay} onCheckedChange={(v: boolean) => setShowFullDay(v)} />
              {t('roomBrowse.showFullDay')}
            </label>
          </div>

          {rooms.length === 0 ? (
            <Banner>{t('roomBrowse.empty')}</Banner>
          ) : visibleRooms.length === 0 ? (
            <Banner>{t('roomBrowse.emptyFiltered')}</Banner>
          ) : (
            <div className="flex flex-col gap-3">
              {visibleRooms.map((room) => {
                const state = perRoom[room.id]
                const isSelected = selection?.roomId === room.id
                return (
                  <LayerCard key={room.id} data-testid={`room-card-${room.id}`}>
                    <LayerCard.Primary>
                      <div className="mb-2 flex items-center gap-3">
                        {room.photoUrl && (
                          <img
                            src={room.photoUrl}
                            alt=""
                            className="h-12 w-12 rounded"
                            style={{ objectFit: 'cover' }}
                          />
                        )}
                        <div>
                          <div className="font-semibold">{room.name}</div>
                          <div className="text-xs text-kumo-subtle">
                            {room.floor ?? ''} · {room.capacity}p · {room.type}
                          </div>
                        </div>
                        {!room.bookable && (
                          <span className="ml-auto text-xs text-kumo-subtle">
                            {t('roomBrowse.notBookable')}
                          </span>
                        )}
                      </div>
                      {state?.loading ? (
                        <div className="flex items-center justify-center py-16">
                          <Loader />
                        </div>
                      ) : state?.error ? (
                        <div className="text-sm">
                          <button
                            type="button"
                            className="text-kumo-link underline"
                            onClick={() => retryRow(room.id)}
                            data-testid={`retry-${room.id}`}
                          >
                            {t('roomBrowse.loadFailed')}
                          </button>
                        </div>
                      ) : (
                        dayStart && (
                          <RoomTimeline
                            slots={state?.slots ?? []}
                            dayStart={dayStart}
                            timezone={tz}
                            startHour={startHour}
                            endHour={endHour}
                            selection={isSelected ? selection!.range : null}
                            onSelect={(next) => setSelectionForRoom(room.id, next)}
                            disabled={!room.bookable}
                            roomName={room.name}
                            now={nowMs}
                            labels={{
                              free: t('roomTimeline.free'),
                              busy: t('roomTimeline.busy'),
                              past: t('roomTimeline.past'),
                              locked: t('roomTimeline.locked'),
                              rangeSep: t('roomTimeline.rangeSep'),
                              availabilityFor: t('roomTimeline.availabilityFor'),
                              selected: (range) =>
                                t('roomTimeline.selected').replace('{range}', range),
                              startAnnouncement: (time) =>
                                t('roomTimeline.startAnnouncement').replace('{time}', time),
                            }}
                          />
                        )
                      )}
                      {isSelected && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-sm">
                            {t('roomBrowse.selectedRange')
                              .replace(
                                '{start}',
                                new Date(selection!.range.startMs).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: tz,
                                }),
                              )
                              .replace(
                                '{end}',
                                new Date(selection!.range.endMs).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: tz,
                                }),
                              )}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectionForRoom(room.id, null)}
                          >
                            {t('roomBrowse.cancel')}
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={submitting}
                            onClick={confirmSelection}
                            data-testid={`confirm-${room.id}`}
                          >
                            {t('roomBrowse.confirm')}
                          </Button>
                        </div>
                      )}
                    </LayerCard.Primary>
                  </LayerCard>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
